import { useEffect, useRef, useState } from "react";
import { addFavourite, removeFavourite } from "../api/client";
import { useAuth } from "../context/AuthContext";

/** Star toggle for a car's favourite status (spec §2.4.8).
 *
 * Filled amber = starred; amber outline = not starred. The two states differ
 * by fill alone, at the same size and stroke, so the column reads as a single
 * scannable band rather than a jumble of shapes.
 *
 * Renders nothing when signed out. Favourites are server state keyed to a
 * user, so there is nowhere to put a star without a session — and showing a
 * dead control the user cannot use is worse than showing none. (Caddy basic
 * auth in front of the app is a different layer entirely; it grants no user.)
 *
 * The toggle is optimistic: the star flips immediately and reverts if the
 * request fails, because the write is a single idempotent PUT/DELETE with no
 * body to reconcile. `initial` re-syncs the control whenever the server sends
 * a new value (a page refetch, a brand switch), but a user click always wins
 * until its own request settles.
 */
export default function FavouriteStar({
  carId,
  initial,
  onChange,
  className = "",
}: {
  carId: string;
  initial: boolean;
  /** Called with the new state after the server confirms it. */
  onChange?: (isFavourite: boolean) => void;
  className?: string;
}) {
  const { user } = useAuth();
  const [on, setOn] = useState(initial);
  const [busy, setBusy] = useState(false);
  const serverValue = useRef(initial);

  // Follow the server only when the server's own answer actually changes.
  //
  // Gating on `!busy` instead (the first attempt) silently undid every
  // successful toggle: the parent list does not refetch on a star click, so
  // `initial` stayed false, and the moment the request settled and `busy`
  // flipped back the effect re-ran and set `on` to that stale false. The star
  // lit up and snapped straight off, which reads as "clicking does nothing" —
  // while the server had in fact saved it every time.
  //
  // Comparing against the previous prop value means a local toggle is never
  // stomped, and a genuine server change (refetch, brand switch, another tab)
  // still wins.
  useEffect(() => {
    if (serverValue.current !== initial) {
      serverValue.current = initial;
      setOn(initial);
    }
  }, [initial]);

  if (!user) {
    return null;
  }

  async function toggle(e: React.MouseEvent) {
    // Rows and cards are clickable in places; the star is not a navigation.
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    const next = !on;
    setOn(next);
    setBusy(true);
    try {
      if (next) {
        await addFavourite(carId);
      } else {
        await removeFavourite(carId);
      }
      onChange?.(next);
    } catch {
      setOn(!next);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={on}
      aria-label={on ? "Remove from favourites" : "Add to favourites"}
      title={on ? "Remove from favourites" : "Add to favourites"}
      className={`disabled:opacity-50 ${
        on ? "text-amber-400 hover:text-amber-500" : "text-gray-400 hover:text-amber-400"
      } ${className}`.trim()}
      disabled={busy}
    >
      {/* Off is a grey outline, on is a filled amber star. Hover previews the
          amber so the control still reads as clickable while off. 30px — half
          again the 20px it started at. */}
      <svg
        viewBox="0 0 24 24"
        className="w-[30px] h-[30px]"
        fill={on ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 3.5l2.7 5.47 6.04.88-4.37 4.26 1.03 6.01L12 17.28l-5.4 2.84 1.03-6.01L3.26 9.85l6.04-.88L12 3.5z" />
      </svg>
    </button>
  );
}
